/**
 * Which tile a tap chose, when the grid may have moved under the finger.
 *
 * The results reflow mid-tap for reasons that have nothing to do with the user.
 * A thumbnail that fails over the network used to remove its tile seconds after
 * the grid rendered — that one is now prevented at the source, since broken
 * tiles keep their slot — but dismissing the phone keyboard resizes the sheet,
 * and that cannot be prevented at all. Either shifts every later tile up a
 * place under a finger that never moved.
 *
 * That breaks tap handling in two directions at once. The browser may cancel
 * the touch outright, so `pointerup` never arrives — and the click it
 * synthesises afterwards is hit-tested against whatever tile has slid under the
 * finger by then, which is a *different picture* from the one that was touched.
 *
 * So a tap is anchored where it started and resolved by whichever event gets
 * here first. Where the finger came up decides nothing; only how far it
 * travelled, which is what separates a choice from a scroll.
 */

/** Past this, the finger was scrolling the grid rather than choosing from it. */
export const TAP_SLOP_PX = 12;
/** So a stale touch can never be claimed by some later, unrelated click. */
export const GESTURE_TTL_MS = 1500;

export interface TapGesture<T> {
	/** What the finger went down on — the only thing that decides the pick. */
	target: T;
	x: number;
	y: number;
	at: number;
	/** Set by the caller once resolved, so the trailing click can't pick twice. */
	done: boolean;
}

export type TapOutcome<T> =
	/** Take it — `target` is the tile the finger actually went down on. */
	| { action: "pick"; target: T }
	/** The trailing click of a tap already handled. Eat it. */
	| { action: "swallow" }
	/** The finger was scrolling, or the record went stale: forget it. */
	| { action: "discard" }
	/** No touch behind this event: the caller should treat it as its own. */
	| { action: "ignore" };

export function resolveTap<T>({
	gesture,
	x,
	y,
	now,
}: {
	gesture: TapGesture<T> | null;
	x: number;
	y: number;
	now: number;
}): TapOutcome<T> {
	if (!gesture) return { action: "ignore" };

	// A spent gesture is only worth swallowing for as long as the browser might
	// still be synthesising a click for it. Past that it is litter, and treating
	// it as pending would silently eat every later mouse click on a device that
	// has both a finger and a pointer.
	const stale = now - gesture.at > GESTURE_TTL_MS;
	if (gesture.done) return { action: stale ? "ignore" : "swallow" };
	if (stale) return { action: "discard" };

	if (Math.hypot(x - gesture.x, y - gesture.y) > TAP_SLOP_PX) {
		return { action: "discard" };
	}
	return { action: "pick", target: gesture.target };
}
